// `badges`: Swift `Badge` (GroundTeam/Models/Engagement.swift): achievements earned by reaching `threshold` of a metric
// within a period. Swift gives none of name/systemImage/metric/threshold/period a property default, so `newBadge`
// requires them; the decoding template `Badge(name: "", systemImage: "star", metric: .visits, threshold: 0,
// period: .weekly)` (GroundTeam/Sync/ResilientDecoding.swift:135) supplies the values for missing keys.

import { GOAL_METRICS, GOAL_PERIODS, type GoalMetric, type GoalPeriod } from '../enums';
import type { JsonObject } from '../wire';
import { newId } from '../uuid';
import {
  DOUBLE,
  STRING,
  UUID,
  createModel,
  decodeModel,
  enumOf,
  field,
  type Decoded,
  type FieldMap,
  type Known,
  type Passthrough,
} from './fields';

export interface BadgePayload extends Passthrough {
  id: string;
  name: string;
  /** SF Symbol name (the web maps it to an icon of its own). */
  systemImage: string;
  metric: GoalMetric;
  /** Double. */
  threshold: number;
  period: GoalPeriod;
}

export const BADGE_KEYS = [
  'id',
  'name',
  'systemImage',
  'metric',
  'threshold',
  'period',
] as const satisfies readonly (keyof Known<BadgePayload>)[];

export const BADGE_FIELDS: FieldMap<BadgePayload> = {
  id: field.strict(UUID),
  name: field.req(STRING, () => ''),
  systemImage: field.req(STRING, () => 'star'),
  metric: field.req(enumOf('GoalMetric', GOAL_METRICS), () => 'visits'),
  threshold: field.req(DOUBLE, () => 0),
  period: field.req(enumOf('GoalPeriod', GOAL_PERIODS), () => 'weekly'),
};

export function decodeBadge(raw: JsonObject, envelopeId?: string): Decoded<BadgePayload> {
  return decodeModel(BADGE_FIELDS, raw, {}, envelopeId);
}

export type BadgeInit = Partial<Known<BadgePayload>> &
  Pick<Known<BadgePayload>, 'name' | 'systemImage' | 'metric' | 'threshold' | 'period'>;

export function newBadge(init: BadgeInit): BadgePayload {
  return createModel(BADGE_FIELDS, { id: newId() }, init, 'badges');
}
